import React, { Component } from 'react';
import Axios from 'axios';
import scrollToComponent from 'react-scroll-to-component';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

/* Icons */
import FilterIcon from '~/../symbols/filter.svg';

/* Config */
import { reactToastifyConfig } from '~/config';

/* Components */
import ProductOverviewHeader from './ProductOverviewHeader';
import ProductOverviewResults from './ProductOverviewResults';
import ProductOverviewFilter from './ProductOverviewFilter';
import ProductOverviewPagination from './ProductOverviewPagination';

/* Context */
import { StaticData } from '~/context/staticData';

/* Utilities */
import { getFormParams, appendPageDatalayer } from '~/utilities';

/* Helpers */
import { ResetFilter } from '../helpers/ResetFilter'

/* Common */
import { Button } from '~/components/common';

/* Utilities */
import { trans } from '~/utilities';

export default class ProductOverview extends Component {
    constructor(props) {
        super(props);

        this.urlParams = new URLSearchParams(location.search);
        if (this.props?.customOptions?.defaultSorting && !this.urlParams.has('sort')) {
            this.urlParams.append('sort', this.props.customOptions.defaultSorting);
        }

        this.resultsRef = React.createRef();
        this.formRef = React.createRef();

        this.matchMedia = window.matchMedia('(min-width: 992px)');

        const headerElement = document.querySelector('.main-header');
        if (headerElement) headerElement.classList.add(['main-header-vehicle-vop']);

        this.state = {
            sorting: [],
            items: [],
            filters: [],
            filterOpen: false,
            totalResults: 0,
            stickerTooltips: {},
            page: this.urlParams.has('page') ? this.urlParams.get('page') : 1,
            totalPages: 0,
            isLoading: true,
            query: this.urlParams.has('query') ? this.urlParams.get('query') : null,
            isSticky: false,
            isDesktop: this.matchMedia.matches,
            modalOpen: false,
            filtersShowAll: false,
        };
        this.matchMedia.addEventListener('change', this.mediaListener);

        toast.configure(reactToastifyConfig);
    }

    mediaListener = (event) => {
        this.setState({ isDesktop: event.matches });
    };

    componentDidMount = () => {
        if (this.urlParams.getAll('sources[]').includes('occasions') && !this.urlParams.getAll('sources[]').includes('demo')) {
            this.urlParams.append('sources[]', 'demo');
            this.replaceHistoryState(getFormParams(this.urlParams));
        }
        this.fetchData(this.state.page, false, this.urlParams, true);
        this.fetchFavorites();
        window.addEventListener('scroll', this.handleScroll);
    }

    componentWillUnmount = () => {
        this.matchMedia.removeEventListener('change', this.mediaListener);
        window.removeEventListener('scroll', this.handleScroll);
    }

    handleScroll = () => {
        const overviewElement = document.querySelector('#product-overview');
        if (overviewElement) {
            if (overviewElement.getBoundingClientRect().top <= -120) {
                !this.state.isSticky && this.setState({ isSticky: true });
            } else {
                this.state.isSticky && this.setState({ isSticky: false });
            }
        }
    }

    fetchFavorites = () => {
        Axios.get('/get-vehicle-favorites')
            .then(response => {
                const favorites = response.data.vehicles;
                this.setState({ favorites: favorites });
            })
            .catch(error => {
                toast.error('Er is iets verkeerd gegaan bij het ophalen van favorieten.');
            });
    };

    fetchData = (page, saveParams = true, formData, initialFetch = false) => {
        this.setState({ items: [], isLoading: true, });

        formData.set('page', page);

        const params = getFormParams(formData);

        if (saveParams) this.replaceHistoryState(params);

        Axios.get(this.props.updateUrl, { params: params }).then((response) => {
            if (response.data) {
                const { content: { sticker_tooltips: stickerTooltips = {} } = {}, sorting, items, filters, count, pages, } = response.data;

                this.setState({
                    sorting: sorting.items,
                    items: items,
                    filters: filters.items,
                    totalResults: count,
                    page: pages.current,
                    totalPages: pages.total,
                    stickerTooltips: stickerTooltips,
                });

                if (initialFetch) {
                    const datalayerItems = items.map((item, itemIndex) => ({
                        index: `${ itemIndex + 1 }`,
                        item_id: `${ item?.id || '' }`,
                        item_id2: `${ item?.attributes?.voertuignr_hexon?.value || '' }`.toLowerCase(),
                        item_awd: `${ item?.attributes?.voertuignr?.value || '' }`.toLowerCase(),
                        item_brand: `${ item?.brand || '' }`.toLowerCase(),
                        item_model: `${ item?.model || '' }`.toLowerCase(),
                        item_license_plate: `${ item?.attributes?.kenteken?.value || '' }`.toLowerCase(),
                        item_body: `${ item?.attributes?.carrosserie?.value || '' }`.toLowerCase(),
                        item_online_date: format(new Date(item?.createdAt), 'yyyy-MM-dd*HH:mm:ss'),
                        item_transmission: `${ item?.attributes?.transmissie?.value || '' }`.toLowerCase(),
                        item_fuel_type: `${ item?.attributes?.brandstof?.value || '' }`.toLowerCase(),
                        item_stock_type: `${ item?.attributes?.source?.value || '' }`.toLowerCase(),
                        item_odemeter_distance: `${ item?.attributes?.tellerstand?.value || '' }`,
                        item_color: `${ item?.attributes?.kleur?.value || '' }`.toLowerCase(),
                        item_construction_year: `${ item?.attributes?.bouwjaar?.value || '' }`,
                        item_type: `${ item?.type || '' }`.toLowerCase(),
                        business_lease_price: `${ item?.enrichedValues?.business_lease || '' }`,
                        private_lease_price: `${ item?.enrichedValues?.private_lease || '' }`,
                        finance_price: `${ item?.enrichedValues?.finance_price || '' }`,
                        price: `${ item?.price || '' }`,
                        sale_price: `${ item?.attributes?.actieprijs?.value || '' }`,
                        affiliation: `${ item?.attributes?.vestiging?.value || '' }`.toLowerCase(),
                        currency: 'EUR',
                    }));

                    appendPageDatalayer({
                        event: 'view_item_list',
                        items: datalayerItems,
                    }, 'vehicleOverviewDatalayer');
                }
            }
        }).catch((error) => {
            toast.error('Er is iets verkeerd gegaan, probeer het nogmaals.');
            throw new Error(error);
        }).finally(() => {
            this.setState({ isLoading: false });
        })

    };

    replaceHistoryState = (params) => {
        history.replaceState({}, document.title, `${ location.pathname }${ [...params].length > 0 ? `?${ params }` : '' }`);
    };

    updateFilters = (type, values) => {
        if (type === 'filters') {
            this.setState({ filters: values }, () => {
                this.fetchData(1, true, new FormData(this.formRef.current));
            });
        }

        if (type === 'sorting') {
            this.setState({ sorting: values });
            this.fetchData(1, true, new FormData(this.formRef.current));
        }
    };

    updateQuery = (value) => {
        this.setState({ query: value }, () => {
            this.fetchData(1, true, new FormData(this.formRef.current));
        });
    };

    updateCurrentPage = (data) => {
        const page = data.selected;
        this.fetchData(page + 1, true, new FormData(this.formRef.current));

        scrollToComponent(this.resultsRef.current, {
            offset: -200,
            align: 'top',
            ease: 'inOutCirc',
        });
    };

    handleResetFilter = (filterKey) => {
        const newFilters = ResetFilter(this.state.filters, filterKey);
        this.setState(prevState => ({ ...prevState, filters: newFilters }), () => {
            this.fetchData(1, true, new FormData(this.formRef.current));
        });
    };

    resetFilters = () => {
        this.setState({ query: "" }, () => {
            this.fetchData(1, true, new FormData());
        });
    };

    setFiltersShowAll = (value) => {
        this.setState({ filtersShowAll: value });
    }

    render() {
        return (
            <StaticData.Provider value={{ customOptions: this?.props?.customOptions }}>
                <form ref={this.formRef} onSubmit={(e) => e.preventDefault()}>
                    <div className="container">
                        <div className={`row gutters-0 mb-6 mb-lg-0`}>
                            {this.state.isDesktop && (
                                <>
                                    <div className="col-12">
                                        <div className="d-flex flex-row align-items-end gap-2 mb-6">
                                            <h6>
                                                {this.props?.customOptions?.title ?
                                                    this.props.customOptions.title :
                                                    trans('app.vehicle_overview.title')
                                                }
                                            </h6>
                                        </div>
                                    </div>
                                    <div className="col-12 px-0">
                                        <ProductOverviewFilter
                                            filters={this.state.filters}
                                            isSticky={this.state.isSticky}
                                            isDesktop={true}
                                            updateFilters={(filters) => this.updateFilters('filters', filters)}
                                            handleResetFilter={this.handleResetFilter}
                                            filtersShowAll={this.state.filtersShowAll}
                                            setFiltersShowAll={this.setFiltersShowAll}
                                        />
                                        <hr className="my-4" />
                                    </div>
                                </>
                            )}
                            <div className="col-12">
                                <ProductOverviewHeader
                                    filters={this.state.filters}
                                    sorting={this.state.sorting}
                                    isLoading={this.state.isLoading}
                                    modalOpen={this.state.modalOpen}
                                    setModalOpen={(value) => this.setState({ modalOpen: value })}
                                    resetAllFilters={this.resetFilters}
                                    updateSorting={(options) => this.updateFilters('sorting', options)}
                                    isDesktop={this.state.isDesktop}
                                    isSticky={this.state.isSticky}
                                    totalResults={this.state.totalResults}
                                    updateFilters={(filters) => this.updateFilters('filters', filters)}
                                    handleResetFilter={this.handleResetFilter}
                                />
                                <div ref={this.resultsRef} className="mb-8 bust-out mx-md-0">
                                    <ProductOverviewResults
                                        isLoading={this.state.isLoading}
                                        items={this.state.items}
                                        favorites={this.state.favorites}
                                        stickerTooltips={this.state.stickerTooltips}
                                        filtersShowAll={this.state.filtersShowAll}
                                        setFiltersShowAll={this.setFiltersShowAll}
                                        review={this.props.review}
                                    />
                                </div>
                                <ProductOverviewPagination
                                    page={this.state.page - 1}
                                    totalPages={this.state.totalPages}
                                    updateCurrentPage={this.updateCurrentPage}
                                />
                            </div>
                            <div className={`btn-filter-sticky mt-10 ${ this.state.isSticky ? 'is-active' : '' } d-lg-none`}>
                                <Button
                                    title={trans('app.vehicle_overview.filter_button')}
                                    theme="primary"
                                    showIcon={true}
                                    icon={FilterIcon}
                                    iconSize={24}
                                    className="d-flex gap-2"
                                    onClick={() => this.setState({ modalOpen: true })}
                                />
                            </div>
                        </div>
                    </div>
                </form>
            </StaticData.Provider>
        );
    }
}
